/**
 * useNotificationsGraphQL Hook
 *
 * Apollo Client hooks for notification queries.
 * Replaces TanStack Query hooks for notifications.
 *
 * Feature: 065-graphql-migration-cleanup
 */

import { useQuery, gql } from '@apollo/client';
import { useAuth } from '@/services/auth';
import logger from '@/utils/logger';
import type { Notification, NotificationStatus, NotificationUrgency, NotificationType } from '@/types/notification';

/**
 * GraphQL Queries
 */
const NOTIFICATIONS_QUERY = gql`
  query Notifications($userId: ID!, $limit: Int, $offset: Int) {
    notifications(userId: $userId, limit: $limit, offset: $offset) {
      items {
        id
        globalId
        userId
        type
        title
        message
        actionUrl
        status
        urgency
        data
        isRead
        createdAt
      }
      totalCount
      hasMore
      limit
      offset
    }
  }
`;

const UNREAD_NOTIFICATION_COUNT_QUERY = gql`
  query UnreadNotificationCount {
    unreadNotificationCount {
      count
      timestamp
    }
  }
`;

/**
 * GraphQL response types
 */
interface GraphQLNotification {
  id: string;
  globalId: string;
  userId: string;
  type: string;
  title: string;
  message: string | null;
  actionUrl: string | null;
  status: string;
  urgency: string;
  data: Record<string, unknown> | null;
  isRead: boolean;
  createdAt: string;
}

interface NotificationsQueryResult {
  notifications: {
    items: GraphQLNotification[];
    totalCount: number;
    hasMore: boolean;
    limit: number;
    offset: number;
  };
}

interface UnreadCountQueryResult {
  unreadNotificationCount: {
    count: number;
    timestamp: string;
  };
}

/**
 * Transform GraphQL notification to frontend Notification type
 */
function transformNotification(gqlNotification: GraphQLNotification): Notification {
  // Parse notification data for additional fields
  const data = gqlNotification.data || {};

  return {
    globalId: gqlNotification.globalId,
    recipientId: gqlNotification.userId,
    actorId: (data.actorId as string) || null,
    notificationType: (data.notificationType as NotificationType) || gqlNotification.type as NotificationType,
    targetType: (data.targetType as string) || '',
    targetId: (data.targetId as string) || '',
    title: gqlNotification.title,
    body: gqlNotification.message || null,
    actionUrl: gqlNotification.actionUrl || null,
    status: gqlNotification.status as NotificationStatus,
    readAt: gqlNotification.isRead ? (data.readAt as string) || null : null,
    urgency: gqlNotification.urgency as NotificationUrgency,
    payload: data,
    createdAt: gqlNotification.createdAt,
    updatedAt: (data.updatedAt as string) || gqlNotification.createdAt,
  };
}

/**
 * Hook: Fetch user notifications with polling support
 */
export function useNotificationsGraphQL(options?: {
  limit?: number;
  offset?: number;
  enabled?: boolean;
  pollInterval?: number;
}) {
  const { user } = useAuth();
  // Use globalId if available, fallback to id (both should be global ID format)
  const userId = user?.globalId || user?.id;

  // Determine if query should be skipped
  // Only skip if no user OR if enabled is explicitly set to false
  const shouldSkip = !userId || options?.enabled === false;

  logger.debug('Notifications', 'useNotificationsGraphQL hook called', {
    userId,
    enabled: options?.enabled,
    shouldSkip,
    limit: options?.limit,
  });

  // CRITICAL: Always provide a valid placeholder ID when skipping to prevent
  // Apollo Client from validating required variables before checking skip.
  // We use 'skip-placeholder' which will never match a real user ID.
  const { data, loading, error, refetch } = useQuery<NotificationsQueryResult>(
    NOTIFICATIONS_QUERY,
    {
      variables: {
        userId: userId || 'skip-placeholder',
        limit: options?.limit || 50,
        offset: options?.offset || 0,
      },
      skip: shouldSkip,
      // Don't poll when there's no user - prevents validation errors from stale polling
      pollInterval: userId ? options?.pollInterval : undefined,
      fetchPolicy: 'cache-and-network',
      onError: (err) => {
        // Don't log errors when we expect them (no user)
        if (userId) {
          logger.error('Notifications', 'Failed to fetch notifications via GraphQL', err);
        }
      },
    }
  );

  logger.debug('Notifications', 'useNotificationsGraphQL result', {
    hasData: !!data,
    itemCount: data?.notifications?.items?.length,
    loading,
    error: error?.message,
  });

  const notifications = data?.notifications.items.map(transformNotification) || [];

  return {
    data: data?.notifications
      ? {
          notifications,
          pagination: {
            total: data.notifications.totalCount,
            limit: data.notifications.limit,
            offset: data.notifications.offset,
            hasMore: data.notifications.hasMore,
          },
        }
      : undefined,
    notifications,
    loading,
    error,
    refetch,
    // Aliases for TanStack Query compatibility
    isLoading: loading,
    isError: !!error,
  };
}

/**
 * Hook: Fetch unread notification count with polling support
 */
export function useUnreadNotificationCountGraphQL(options?: {
  pollInterval?: number;
  enabled?: boolean;
}) {
  const { user } = useAuth();
  const shouldSkip = !user || options?.enabled === false;

  const { data, loading, error, refetch } = useQuery<UnreadCountQueryResult>(
    UNREAD_NOTIFICATION_COUNT_QUERY,
    {
      skip: shouldSkip,
      // Don't poll when there's no user - prevents unnecessary requests and errors
      pollInterval: user ? (options?.pollInterval || 30000) : undefined,
      fetchPolicy: 'cache-and-network',
      onError: (err) => {
        // Don't log errors when we expect them (no user) or poll not-modified responses
        if (user && err.message !== 'NOT_MODIFIED') {
          logger.error('Notifications', 'Failed to fetch unread count via GraphQL', err);
        }
      },
    }
  );

  return {
    data: data?.unreadNotificationCount
      ? {
          count: data.unreadNotificationCount.count,
          timestamp: data.unreadNotificationCount.timestamp,
        }
      : undefined,
    count: data?.unreadNotificationCount?.count || 0,
    loading,
    error,
    refetch,
    // Aliases for TanStack Query compatibility
    isLoading: loading,
    isError: !!error,
  };
}

// Re-export mutations
export {
  useMarkNotificationReadGraphQL,
  useMarkAllNotificationsReadGraphQL,
} from '../../hooks/graphql/useNotificationMutationsGraphQL';
